import { ListItemCardMobile } from "@components/common/mobile/list-item-card-mobile";
import { MenuItemOption } from "@components/common/mobile/mobile-types";
import { ProjectDateTooltip } from "@components/common/project-card/project-date-tooltip";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { PROJECTS_SORT_BY_MAP } from "@components/projects/projects-sort-constants";
import { BaseProjectProps, SdbProject } from "@custom-types/project-types";
import { AccessLevelDisplayNames } from "@components/access-level/access-level-utils";
import { NO_TRANSLATE_CLASS, Text } from "@flat-ui";
import { useDateTime } from "@hooks/use-date-time";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { withEllipsis } from "@styles/common-styles";
import {
  defaultProjectThumbnail,
  getProjectDateBySort,
  getProjectDateLabel,
  getProjectSize,
  shouldHideOpenProject,
} from "@utils/project-utils";
import { DASH } from "@utils/ui-utils";
import { ProjectOpenButton } from "@components/project/project-open-button";
import { ProjectActionContext } from "@components/common/project-actions";
import { useMediaQueryList } from "@hooks/use-media-query";

interface Props extends BaseProjectProps {
  /** Current sort criteria */
  sortBy: FaroCoreApiTypes.ProjectsSortBy;

  /** Context in which the component is being used */
  contextType: ProjectActionContext;

  /** Optional URL; when provided, tile renders as a link instead of a clickable div */
  href?: string;

  /** Optional callback triggered when the card is clicked. Can coexist with href. */
  onCardClick?: () => void;

  /** Optional menu options; when provided a three-dots menu is shown. */
  menuItems?: MenuItemOption<SdbProject>[];
}

/**
 * @returns A card with information of the passed project for the project list in mobile view.
 */
export function ProjectListItemCardMobile({
  project,
  sortBy,
  contextType,
  href,
  onCardClick,
  menuItems,
}: Props): JSX.Element {
  const { isScreenXs } = useMediaQueryList();
  const { formatDateWithTooltip } = useDateTime();
  const sortField = PROJECTS_SORT_BY_MAP[sortBy].field;
  const dateLabel = getProjectDateLabel(sortField);
  const rawDateValue = getProjectDateBySort(project, sortField);
  const formattedDate = rawDateValue
    ? formatDateWithTooltip(rawDateValue).displayDate
    : DASH;

  const accessLevel = AccessLevelDisplayNames[project.accessLevel];
  const size = getProjectSize(project);
  const shouldShowSize = size !== "-";
  const secondaryText = shouldShowSize
    ? `${accessLevel} • ${size}`
    : accessLevel;

  // Only show the open project button for screens larger than xs, this is because
  // for extra small screens there is not much space for the button
  const shouldShowOpenProjectButton =
    !shouldHideOpenProject(project) && !isScreenXs;

  return (
    <ListItemCardMobile<SdbProject>
      item={project}
      image={project.thumbnailUrl ?? defaultProjectThumbnail}
      imageAlt="Project thumbnail"
      title={
        <SphereTooltip
          shouldSkipWrapper
          title={<var>{project.name}</var>}
          boxProps={{
            className: NO_TRANSLATE_CLASS,
            sx: {
              marginTop: "8px",
              ...withEllipsis,
            },
          }}
        >
          {project.name}
        </SphereTooltip>
      }
      href={href}
      onCardClick={onCardClick}
      subtitle={
        <Text variant="bodyS">
          {dateLabel}
          <span className={NO_TRANSLATE_CLASS}>: {formattedDate}</span>
        </Text>
      }
      subtitleTooltip={<ProjectDateTooltip project={project} />}
      secondaryText={secondaryText}
      actions={
        shouldShowOpenProjectButton ? (
          <ProjectOpenButton project={project} contextType={contextType} />
        ) : undefined
      }
      menuItems={menuItems}
    />
  );
}
